<template>
    <PageWrapper title="Help" description="Guides and reference for the platform">
        <div class="KbHelpView">
            <KbSearchBox class="KbHelpView__search" @select="goToArticle" />

            <div v-if="isLoading" class="KbHelpView__loading">
                <span class="KbHelpView__srOnly">Loading help articles…</span>
                <div v-for="n in skeletonCardCount" :key="n" class="KbHelpView__skeletonCard">
                    <div class="KbHelpView__skeletonHeader">
                        <div class="KbHelpView__skeletonIcon" />
                        <div class="KbHelpView__skeletonLine KbHelpView__skeletonLine--title" />
                    </div>
                    <div class="KbHelpView__skeletonLine KbHelpView__skeletonLine--description" />
                    <div class="KbHelpView__skeletonLine KbHelpView__skeletonLine--article" />
                    <div class="KbHelpView__skeletonLine KbHelpView__skeletonLine--article" />
                    <div class="KbHelpView__skeletonLine KbHelpView__skeletonLine--article" />
                </div>
            </div>

            <div v-else-if="hasError" class="KbHelpView__error">
                <EidIcon class="KbHelpView__errorIcon" :icon-src="faTriangleExclamation" />
                <p class="KbHelpView__errorTitle">
                    We could not load help articles right now.
                </p>
                <p class="KbHelpView__errorDescription">
                    Check your connection and try again.
                </p>
                <NewButton size="sm" color="primary" @click="reload">
                    Try again
                </NewButton>
            </div>

            <div v-else-if="!orderedSections.length" class="KbHelpView__empty">
                <EidIcon class="KbHelpView__emptyIcon" :icon-src="faBookOpen" />
                <p class="KbHelpView__emptyText">
                    No help articles are available yet.
                </p>
            </div>

            <div v-else class="KbHelpView__grid">
                <div
                    v-for="(column, index) in sectionColumns"
                    :key="index"
                    class="KbHelpView__column"
                >
                    <KbSectionTree
                        v-for="section in column"
                        :key="section.id"
                        :section="section"
                    />
                </div>
            </div>
        </div>
    </PageWrapper>
</template>

<style lang="scss">
.KbHelpView {
    display: flex;
    flex-direction: column;
    gap: 24px;

    &__search {
        max-width: 480px;
    }

    &__srOnly {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    &__grid {
        /* Real columns rather than CSS `columns`, because that balances on height: one tall
           section leaves the first column holding a single card while a later one holds three.
           The component decides which section goes in which column and fills from the left;
           this only has to lay the columns out and let them wrap on narrow screens. */
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 20px;
        max-width: 1340px;
    }

    &__column {
        display: flex;
        flex: 1 1 320px;
        flex-direction: column;
        gap: 20px;
    }

    &__loading {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 20px;
        max-width: 1340px;
    }

    &__error,
    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 48px 24px;
        text-align: center;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 10px;
    }

    &__errorIcon,
    &__emptyIcon {
        font-size: 28px;
        color: var(--eidTechGray-75);
    }

    &__errorTitle {
        margin: 0;
        font-size: var(--text-section-header-size, 16px);
        font-weight: 700;
        color: var(--eidSecureBlue);
    }

    &__errorDescription,
    &__emptyText {
        margin: 0;
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue-75);
    }

    &__skeletonCard {
        display: flex;
        flex: 1 1 320px;
        flex-direction: column;
        gap: 10px;
        padding: 20px 24px;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 10px;
    }

    &__skeletonHeader {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 4px;
    }

    &__skeletonIcon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        background: var(--eidTechGray-25);
        animation: kbHelpViewSkeletonPulse 1.4s ease-in-out infinite;
    }

    &__skeletonLine {
        height: 12px;
        border-radius: 4px;
        background: var(--eidTechGray-25);
        animation: kbHelpViewSkeletonPulse 1.4s ease-in-out infinite;

        &--title {
            flex: 1;
            height: 16px;
            max-width: 60%;
        }

        &--description {
            width: 85%;
        }

        &--article {
            width: 70%;
        }
    }
}

@keyframes kbHelpViewSkeletonPulse {
    0%, 100% {
        opacity: 0.6;
    }

    50% {
        opacity: 1;
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { faBookOpen, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { KbSection } from '@evidentid/tprm-portal-lib/models/kb';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { currentRpId } from '@/modules/kb/components/KbArticleContent/helpers';
    import KbSearchBox from '@/modules/kb/components/KbSearchBox/KbSearchBox.vue';
    import KbSectionTree from '@/modules/kb/components/KbSectionTree/KbSectionTree.vue';
    import { useKbStore } from '@/modules/kb/store';

    // Enough placeholder cards to fill the grid at typical viewport widths without the
    // skeleton visibly running out before the real tree arrives.
    const SKELETON_CARD_COUNT = 6;

    // Matches the widest the CSS lets the row of columns get. Kept in both places because the
    // split happens here while the collapse to fewer columns happens in the stylesheet.
    const MaxColumns = 4;

    /* Rough pixel costs of the parts of a section card, read off `KbSectionTree`'s stylesheet.
       These only have to rank cards against each other well enough to balance the columns, so
       an estimate beats measuring the DOM: measuring would mean rendering first, then moving
       cards between columns, which the reader sees as a jump. */
    const CardChromeHeight = 63; // vertical padding, border, and the header line
    const NestedChromeHeight = 21; // a nested section drops the padding and border, keeping the header
    const CardGapHeight = 12; // the card's own `gap`, between header, description and articles
    const ArticleRowHeight = 38; // an article link plus the 2px gap under it
    const TextLineHeight = 21; // one wrapped line of description
    const DescriptionCharsPerLine = 44; // at the 320px the columns are sized around

    /** Approximates a section card's rendered height, for balancing the columns.
     *
     * Counts what actually drives the height: the header, a wrapped description, one row per
     * article, and any nested subsections, which render inside the same card.
     */
    function estimateSectionHeight(section: KbSection, nested = false): number {
        let height = nested ? NestedChromeHeight : CardChromeHeight;

        if (section.description) {
            const lines = Math.max(1, Math.ceil(section.description.length / DescriptionCharsPerLine));
            height += CardGapHeight + lines * TextLineHeight;
        }

        if (section.articles.length) {
            height += CardGapHeight + section.articles.length * ArticleRowHeight;
        }

        return section.sections.reduce(
            (total, child) => total + CardGapHeight + estimateSectionHeight(child, true),
            height,
        );
    }

    export default defineComponent({
        name: 'KbHelpView',
        components: {
            EidIcon, KbSearchBox, KbSectionTree, NewButton, PageWrapper,
        },
        data() {
            return {
                faBookOpen,
                faTriangleExclamation,
                skeletonCardCount: SKELETON_CARD_COUNT,
            };
        },
        computed: {
            kbStore() {
                return useKbStore();
            },
            // Named routes in vue-router 4 don't inherit params from the current route,
            // so a programmatic push has to carry rpId explicitly.
            rpId(): string | undefined {
                return currentRpId(this.$route);
            },
            isLoading(): boolean {
                return [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.kbStore.treeStatus);
            },
            hasError(): boolean {
                return this.kbStore.treeStatus === OperationStatus.error;
            },
            // Categories are flattened into one section list because the landing page
            // browses by section, not by the category that groups them in Zendesk.
            // Zendesk position is scoped to the parent, so it only ever orders
            // categories against each other or sections within the same category —
            // never sections across different categories.
            orderedSections(): KbSection[] {
                const byPosition = (a: { position: number }, b: { position: number }) => a.position - b.position;
                return this.kbStore.tree
                    .slice()
                    .sort(byPosition)
                    .flatMap((category) => [ ...category.sections ].sort(byPosition));
            },
            /** Splits the sections into columns, each one going to the shortest column so far.
             *
             * CSS columns balance on height rather than count, so a single tall section can
             * leave the first column holding one card while the third holds three. Packing the
             * columns here instead measures each section by what actually drives its height, so
             * one long section is balanced against several short ones. Ties go to the leftmost
             * column, which fills the columns left to right while they are all still empty.
             */
            sectionColumns(): KbSection[][] {
                const sections = this.orderedSections;
                const columnCount = Math.min(MaxColumns, sections.length);
                if (columnCount === 0) {
                    return [];
                }

                // Each section goes to whichever column is currently shortest, so one section
                // with many articles is balanced against several small ones rather than just
                // counted the same. Ties go to the leftmost column, which is what fills the
                // columns left to right while they are all still empty.
                const columns: KbSection[][] = Array.from({ length: columnCount }, () => []);
                const heights = new Array<number>(columnCount).fill(0);

                sections.forEach((section) => {
                    let shortest = 0;
                    for (let column = 1; column < columnCount; column += 1) {
                        if (heights[column] < heights[shortest]) {
                            shortest = column;
                        }
                    }
                    columns[shortest].push(section);
                    heights[shortest] += estimateSectionHeight(section);
                });

                return columns;
            },
        },
        created(): void {
            this.kbStore.loadTree();
        },
        methods: {
            reload(): void {
                this.kbStore.loadTree({ force: true });
            },
            goToArticle(articleId: string): void {
                this.$router.push({ name: 'kbArticle', params: { rpId: this.rpId, articleId } });
            },
        },
    });
</script>
